/** Split fecha hora. */
export function splitFechaHora(fechaHora: string): { fecha: string; hora: string } {
  if (!fechaHora) return { fecha: '', hora: '' };
  const [d, tRaw] = fechaHora.split('T');
  const hora = (tRaw ?? '').slice(0, 5);
  return { fecha: d ?? '', hora };
}
/** Format fecha pdf. */


export function formatFechaPdf(isoDate: string): string {
  const [y, m, d] = isoDate.split('-');
  if (y && m && d) return `${d}/${m}/${y}`;
  return isoDate || '—';
}
/** Format hora12. */


export function formatHora12(hora24: string): string {
  const [hStr, mStr = '00'] = hora24.split(':');
  let h = Number(hStr);
  const mi = mStr.slice(0, 2);
  if (!Number.isFinite(h)) return hora24 || '—';
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${mi} ${ampm}`;
}

/** Record date/time for PDF headers (DD/MM/YYYY HH:MM). */
export function formatFechaHoraRegistroPdf(fechaRaw: string): string {
  const s = fechaRaw.trim();
  if (!s) return '—';
  let d: Date;
  if (s.includes('T')) {
    d = new Date(s.length === 16 ? `${s}:00` : s);
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    d = new Date(`${s}T12:00:00`);
  } else {
    d = new Date(s);
  }
  if (Number.isNaN(d.getTime())) return s;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}
/** Texto mayus es. */


export function textoMayusEs(text: string): string {
  return text.trim().toLocaleUpperCase('es-CO');
}

/** FO-GESR responsable cell: display name only; never render numeric `usuarios.id`. */
export function responsableLabelForPdf(
  responsableId: number,
  responsableNombre: string | null | undefined,
  resolveById?: (id: number) => string | null | undefined,
): string {
  const direct = (responsableNombre ?? '').trim();
  if (direct) return textoMayusEs(direct);
  const id = Math.floor(Number(responsableId));
  if (id > 0 && resolveById) {
    const looked = (resolveById(id) ?? '').trim();
    if (looked) return textoMayusEs(looked);
  }
  return '—';
}
/** Whether ascii whitespace. */


function isAsciiWhitespace(ch: string): boolean {
  return ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === '\f' || ch === '\v';
}
/** Skip ascii ws from. */


function skipAsciiWsFrom(t: string, i: number): number {
  let p = i;
  while (p < t.length && isAsciiWhitespace(t.charAt(p))) p += 1;
  return p;
}
/** Digit run end. */


function digitRunEnd(t: string, start: number): number | null {
  let p = start;
  while (p < t.length) {
    const ch = t.charAt(p);
    if (ch < '0' || ch > '9') break;
    p += 1;
  }
  return p === start ? null : p;
}
/** Whether comuna dash. */


function isComunaDash(ch: string): boolean {
  return ch === '—' || ch === '–' || ch === '-';
}
/** Parses comuna nombre. */


function parseComunaNombre(t: string): { num: string; resto: string } | null {
  const prefix = 'comuna';
  if (t.length < prefix.length) return null;
  if (t.slice(0, prefix.length).toLowerCase() !== prefix) return null;
  const numStart = skipAsciiWsFrom(t, prefix.length);
  const afterDigits = digitRunEnd(t, numStart);
  if (afterDigits === null) return null;
  const num = t.slice(numStart, afterDigits);
  let i = skipAsciiWsFrom(t, afterDigits);
  if (i >= t.length) return null;
  const dash = t.charAt(i);
  if (!isComunaDash(dash)) return null;
  i = skipAsciiWsFrom(t, i + 1);
  const resto = t.slice(i);
  if (resto.length === 0) return null;
  return { num, resto };
}
/** Comuna ocorregimiento pdf. */


export function comunaOCorregimientoPdf(nombreComuna: string): string {
  const t = nombreComuna.trim();
  const parsed = parseComunaNombre(t);
  if (parsed) {
    return `${parsed.num} ${parsed.resto.trim()}`.toLocaleUpperCase('es-CO');
  }
  return textoMayusEs(t);
}
/** Tipo bono celda pdf. */


export function tipoBonoCeldaPdf(comunaEtiqueta: string): string {
  const u = comunaEtiqueta.trim();
  const prefix = /^(\d+)\s+/.exec(u);
  if (prefix && u.length > prefix[0].length) {
    return `COMUNA ${prefix[1]} -\nBONO\nUNIPERSONAL\nCON PERSONAS\nMAYORES`;
  }
  return `${u}\nBONO\nUNIPERSONAL\nCON PERSONAS\nMAYORES`;
}
/** True when raw has a spaced " DE " token at index i. */


function isDeSeparatorAt(raw: string, i: number): boolean {
  if (i < 1 || i > raw.length - 3) return false;
  if (raw.charAt(i) !== 'D' || raw.charAt(i + 1) !== 'E') return false;
  if (!isAsciiWhitespace(raw.charAt(i - 1))) return false;
  const afterE = i + 2;
  return afterE < raw.length && isAsciiWhitespace(raw.charAt(afterE));
}
/** Trim ascii ws left. */


function trimAsciiWsLeft(raw: string, endInclusive: number): number {
  let leftEnd = endInclusive;
  while (leftEnd >= 0 && isAsciiWhitespace(raw.charAt(leftEnd))) leftEnd -= 1;
  return leftEnd;
}
/** Split raw on spaced " DE " tokens. */


function splitOnDeSeparators(raw: string): string[] {
  const parts: string[] = [];
  let start = 0;
  let i = 0;
  while (i < raw.length - 2) {
    if (isDeSeparatorAt(raw, i)) {
      const afterE = i + 2;
      const leftEnd = trimAsciiWsLeft(raw, i - 1);
      parts.push(raw.slice(start, leftEnd + 1));
      start = skipAsciiWsFrom(raw, afterE);
      i = start;
      continue;
    }
    i += 1;
  }
  parts.push(raw.slice(start));
  return parts;
}
/** Whitespaces to newlines. */


function whitespacesToNewlines(s: string): string {
  let r = '';
  let pendingNl = false;
  for (const ch of s) {
    if (isAsciiWhitespace(ch)) {
      pendingNl = true;
    } else {
      if (pendingNl && r.length > 0) r += '\n';
      pendingNl = false;
      r += ch;
    }
  }
  return r;
}
/** Multi-line ID type cell (official FO layout). */


function tipoDocumentoMultilinea(tipo: string): string {
  const raw = tipo.trim().toLocaleUpperCase('es-CO');
  if (!raw) return '—';
  const parts = splitOnDeSeparators(raw);
  if (parts.length === 2) return `${parts[0]} DE\n${parts[1]}`;
  return whitespacesToNewlines(raw);
}
/** Document number + ID type stacked for table cells. */


export function celdaDocumentoYTipo(documento: string, tipo: string): string {
  const doc = (documento || '').trim() || '—';
  return `${doc}\n${tipoDocumentoMultilinea(tipo)}`;
}
/** YES / NO for simple checkboxes on PDFs. */


export function siNo(v: boolean): string {
  return v ? 'SI' : 'NO';
}
/** Doc electronico id from public id. */


export function docElectronicoIdFromPublicId(publicId: string): string {
  const raw = publicId.replaceAll(/[^a-fA-F0-9]/g, '');
  if (raw.length >= 8) return raw.slice(0, 8).toLowerCase();
  const c = globalThis.crypto;
  const alt =
    c != null && 'randomUUID' in c && typeof c.randomUUID === 'function'
      ? c.randomUUID().replaceAll('-', '')
      : String(Date.now());
  return alt.slice(0, 8).toLowerCase();
}
